<template>
  <div class="backtest-history">
    <div class="history-header">
      <h4>Test History</h4>
      <span v-if="loading" class="loading-indicator">Loading...</span>
    </div>

    <div v-if="loading && tests.length === 0" class="no-tests">
      <p>Loading tests...</p>
    </div>

    <div v-else-if="tests.length === 0" class="no-tests">
      <p>No tests yet</p>
    </div>

    <div v-else class="test-list">
      <div
        v-for="test in displayedTests"
        :key="test.Id"
        :class="['test-item', { active: test.Id === selectedTestId }]"
        @click="$emit('select-test', test.Id)"
      >
        <div class="test-header">
          <div class="test-title">
            <span class="test-name">Test #{{ test.Id }}</span>
          </div>
          <div class="test-right">
            <span v-if="test.Status === 'Processing'" class="test-progress">
              <span class="progress-text">{{ test.Progress }}%</span>
            </span>
            <span
              :class="['test-status', `status-${test.Status.toLowerCase()}`]"
            >
              {{ test.Status }}
            </span>
          </div>
        </div>

        <div class="test-meta">
          <div class="test-header-row">
            <div class="test-date">
              {{ formatDate(test.CreatedAt) }}
            </div>
            <div v-if="test.JobType" class="test-algorithm-type">
              {{ test.JobType }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <button v-if="hasMore" class="btn btn-link show-more-btn" @click="loadMore">
      Show more
    </button>
  </div>
</template>

<script>
export default {
  name: 'BackTestHistory',
  props: {
    tests: {
      type: Array,
      default: () => []
    },
    selectedTestId: {
      type: [String, Number],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {}
  },
  computed: {
    displayedTests() {
      return this.tests
    },
    hasMore() {
      return this.tests.length < this.totalCount
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    loadMore() {
      this.$emit('load-more')
    }
  }
}
</script>

<style scoped lang="scss">
.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  h4 {
    color: var(--sidebar-t);
    font-size: 12px;
    font-weight: 600;
    margin: 0;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .loading-indicator {
    color: #60a5fa;
    font-size: 11px;
  }
}
.backtest-history {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.45) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    min-height: 40px;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.45);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #60a5fa;
  }

  .no-tests {
    text-align: center;
    color: var(--sidebar-t2);
    padding: 24px 12px;
    font-size: 12px;
  }

  .test-list {
    .test-item {
      padding: 11px 12px;
      margin-bottom: 8px;
      border: 1px solid rgba(148, 163, 184, 0.18);
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease, border-color 0.15s ease;
      background: rgba(42, 51, 71, 0.65);

      &:hover {
        border-color: #64748b;
        background: var(--sidebar2);
      }

      &.active {
        border-color: #60a5fa;
        background: rgba(59, 130, 246, 0.18);
        box-shadow: inset 3px 0 0 var(--sidebar-a);
      }

      .test-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .test-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .test-name {
          color: var(--sidebar-t);
          font-weight: 600;
          font-size: 12px;
        }

        .test-right {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .test-progress {
          font-size: 12px;
          color: #fbbf24;
          font-weight: 600;

          .progress-text {
            font-size: 12px;
          }
        }

        .test-status {
          font-size: 12px;
          padding: 2px 7px;
          border-radius: 999px;
          font-weight: 600;

          &.status-completed {
            background: rgba(34, 197, 94, 0.16);
            color: #86efac;
          }

          &.status-failed {
            background: rgba(239, 68, 68, 0.16);
            color: #fca5a5;
          }

          &.status-processing {
            background: rgba(245, 158, 11, 0.16);
            color: #fcd34d;
          }

          &.status-queued {
            background: rgba(56, 189, 248, 0.16);
            color: #7dd3fc;
          }
        }
      }

      .test-meta {
        .test-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .test-date {
          color: var(--sidebar-t2);
          font-size: 10px;
        }

        .test-algorithm-type {
          max-width: 145px;
          overflow: hidden;
          color: var(--sidebar-t2);
          font-size: 10px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  .show-more-btn {
    width: 100%;
    color: #93c5fd;
    font-size: 12px;
  }
}
</style>
